import React, { useState, ChangeEvent, FormEvent } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import { getEncodedTimestamp } from "../../utilities/dateUtils";
import { showToast } from "../../components/Toast";
import { RegisterData } from "../../types/auth.types";
// import "./RegisterModal.css";

import {
  saveGuestId,
  saveUserId
} from "../../utilities/authUtils";

import {
  resolveUid,       // NEW — replaces enforceUserState()
  getUserType
} from "../../utilities/cookieUtils";

import { syncCookieDataToBackend } from "../../api/cartWishlistSyncService";
import PKSOFT_URLS from "../../utilities/Pk_api_Urls";

interface Props {
  show: boolean;
  onHide: () => void;
  isGuest?: boolean;
  onSuccess?: () => void;
  title?: string;
}

const RegisterModal: React.FC<Props> = ({
  show,
  onHide,
  isGuest = false,
  onSuccess,
  title
}) => {
  const dispatch = useDispatch();

  const [form, setForm] = useState<RegisterData>({
    fullName: "",
    emailId: "",
    mobile: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpToken, setOtpToken] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  // Auto-focus first OTP input when OTP screen appears
  React.useEffect(() => {
    if (isOtpScreen) {
      const firstInput = document.getElementById("otp-0");
      firstInput?.focus();
    }
  }, [isOtpScreen]);

  /* ----------------------- INPUT HANDLER ----------------------- */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ----------------------- VALIDATION ----------------------- */
  const validate = () => {
    const e: any = {};
    if (!form.fullName.trim()) e.fullName = "Name required";
    if (!form.emailId.trim()) e.emailId = "Email required";
    if (!/^[0-9]{10}$/.test(form.mobile)) e.mobile = "Enter valid 10 digit mobile";
    return e;
  };

  /* ----------------------- SUBMIT USER DATA ----------------------- */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;

    setIsSaving(true);

    const payload = {
      fullName: form.fullName,
      emailId: form.emailId,
      mobile: form.mobile,
      d: getEncodedTimestamp(),
    };

    const apiURL = isGuest
      ? PKSOFT_URLS.GUEST.GUEST_REGISTER
      : PKSOFT_URLS.CUSTOMER.REGISTER;

    try {
      const res = await axios.post(apiURL, payload);

      if (res.data.messageCode === 100) {
        showToast({ type: "success", message: res.data.message });

        setOtpToken(res.data.data?.token || "");

        /* ----------- SAVE guestId immediately (guest registration) ----------- */
        if (isGuest) {
          const guestId =
            res.data.data?.guestId ||
            res.data.data?.id ||
            res.data.data?.uuid;

          if (guestId) {
            saveGuestId(guestId);     // cookie: guest_id = {id}
            resolveUid();             // cleanup random_user immediately
          }
        }

        setIsOtpScreen(true);
      }
    } catch (err: any) {
      showToast({
        type: "error",
        message: err.response?.data?.message || "Registration failed."
      });
    } finally {
      setIsSaving(false);
    }
  };

  /* ----------------------- VERIFY OTP ----------------------- */
  const handleVerifyOtp = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 6)
      return showToast({ type: "error", message: "Enter 6 digit OTP" });

    const verifyURL = isGuest
      ? PKSOFT_URLS.GUEST.GUEST_OTP
      : PKSOFT_URLS.CUSTOMER.REGISTER_OTP;

    setIsVerifying(true);
    try {
      const res = await axios.post(verifyURL, {
        emailId: form.emailId,
        otpCode,
        token: otpToken,
        postingDate: getEncodedTimestamp(),
      });

      if (res.data.messageCode === 100) {
        showToast({ type: "success", message: "OTP Verified ✅" });

        const data = res.data.data || {};

        const id =
          data.guestId ||
          data.userId ||
          data.id ||
          data.uid ||
          data.uuid;

        if (id) {
          if (isGuest) {
            /* ============================
               GUEST VERIFIED
            ============================ */
            saveGuestId(id);
            resolveUid();  // cleans random_user
            await syncCookieDataToBackend(id, "guest");

            dispatch({
              type: "auth/setUserInfo",
              payload: { guest_id: id }
            });

          } else {
            /* ============================
               USER VERIFIED
            ============================ */
            saveUserId(id);
            resolveUid();  // cleans guest/random
            await syncCookieDataToBackend(id, "user");

            dispatch({
              type: "auth/setUserInfo",
              payload: {
                user_id: id,
                email: form.emailId,
                name: form.fullName,
              },
            });
          }
        }

        onSuccess?.();
        onHide();

        window.dispatchEvent(new Event("loginSuccess"));

        setTimeout(() => window.location.reload(), 500);
      } else {
        showToast({
          type: "error",
          message: res.data.message || "Invalid OTP",
        });
        // Clear OTP fields on error
        setOtp(Array(6).fill(""));
        document.getElementById("otp-0")?.focus();
      }
    } catch (err: any) {
      showToast({
        type: "error",
        message: err.response?.data?.message || "Invalid OTP",
      });
      // Clear OTP fields on exception
      setOtp(Array(6).fill(""));
      document.getElementById("otp-0")?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  /* ----------------------- RENDER ----------------------- */
  return (
    <Modal
      show={show}
      onHide={isSaving ? undefined : onHide}
      centered
      backdrop={isSaving ? "static" : true}
      keyboard={!isSaving}
    >
      {!isOtpScreen ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title>
              {title || (isGuest ? "Guest Checkout" : "Register")}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <form onSubmit={handleSubmit}>
              <div className="mb-2">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name"
                  className={`form-control ${errors.fullName ? "is-invalid" : ""
                    }`}
                  onChange={handleChange}
                />
                {errors.fullName && (
                  <div className="invalid-feedback d-block">
                    {errors.fullName}
                  </div>
                )}
              </div>

              <div className="mb-2">
                <input
                  type="email"
                  name="emailId"
                  placeholder="Email"
                  className={`form-control ${errors.emailId ? "is-invalid" : ""
                    }`}
                  onChange={handleChange}
                />
                {errors.emailId && (
                  <div className="invalid-feedback d-block">
                    {errors.emailId}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <input
                  type="text"
                  name="mobile"
                  placeholder="Mobile"
                  className={`form-control ${errors.mobile ? "is-invalid" : ""
                    }`}
                  onChange={handleChange}
                />
                {errors.mobile && (
                  <div className="invalid-feedback d-block">
                    {errors.mobile}
                  </div>
                )}
              </div>

              <Button type="submit" className="btn btn-danger w-100">
                {isSaving ? "Submitting..." : isGuest ? "Continue" : "Register"}
              </Button>
            </form>
          </Modal.Body>
        </>
      ) : (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Enter OTP</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="d-flex justify-content-center gap-2 mb-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  placeholder={`${index + 1}`}
                  title={`OTP digit ${index + 1}`}
                  className="form-control text-center otp-input"
                  value={digit}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^[0-9]?$/.test(value)) return;

                    const newOtp = [...otp];
                    newOtp[index] = value;
                    setOtp(newOtp);

                    // Move to next input if digit entered
                    if (value && index < 5) {
                      document.getElementById(`otp-${index + 1}`)?.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace") {
                      if (otp[index]) {
                        // If current field has value, clear it
                        const newOtp = [...otp];
                        newOtp[index] = "";
                        setOtp(newOtp);
                      } else if (index > 0) {
                        // If current field is empty, move to previous and clear it
                        const newOtp = [...otp];
                        newOtp[index - 1] = "";
                        setOtp(newOtp);
                        const prevInput = document.getElementById(`otp-${index - 1}`);
                        prevInput?.focus();
                      }
                    }
                  }}
                  disabled={isVerifying}
                />
              ))}
            </div>

            <Button
              id="verify-btn"
              className="btn btn-success w-100"
              onClick={handleVerifyOtp}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Verifying...
                </>
              ) : (
                "Verify OTP"
              )}
            </Button>
          </Modal.Body>
        </>
      )}
    </Modal>
  );
};

export default RegisterModal;
